import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import api from '../api/axios';
import { Building2, ArrowLeft, Search, Download, Loader2, User, Fuel, TrendingUp, DollarSign, ChevronRight, CheckSquare, Square, Filter, Car, Users, Lock, FileText, MapPin } from 'lucide-react';

const formatCurrency = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
const formatNumber = (v) => (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 1 });

// Cores dos postos (mesmo da Lista de Abastecimentos)
const POSTO_COLORS = {
  'POSTO JP&HG LOBO': { bg: '#10B981', text: 'white' },
  'POSTO CARAUARI': { bg: '#EF4444', text: 'white' },
  'REDE BONS AMIGOS': { bg: '#3B82F6', text: 'white' },
  'PONTÃO BARROTE': { bg: '#6B7280', text: 'white' },
};

// Get current month date range
const now = new Date();
const firstDay = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
const lastDayStr = `${lastDay.getFullYear()}-${String(lastDay.getMonth() + 1).padStart(2, '0')}-${String(lastDay.getDate()).padStart(2, '0')}`;

export default function FiltroIndividualPage() {
  const [view, setView] = useState('secretarias');
  const [secretarias, setSecretarias] = useState([]);
  const [selectedSecretaria, setSelectedSecretaria] = useState(null);
  const [searchSecretaria, setSearchSecretaria] = useState('');

  // Filter state
  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(lastDayStr);
  const [funcionarios, setFuncionarios] = useState([]);
  const [combustiveis, setCombustiveis] = useState([]);
  const [combustiveisDisponiveis, setCombustiveisDisponiveis] = useState([]);
  const [veiculos, setVeiculos] = useState([]);
  const [postos, setPostos] = useState([]);
  const [selectedFuncionarios, setSelectedFuncionarios] = useState(new Set());
  const [selectedCombustiveis, setSelectedCombustiveis] = useState(new Set());
  const [selectedVeiculos, setSelectedVeiculos] = useState(new Set());
  const [selectedPostos, setSelectedPostos] = useState(new Set());
  const [searchFunc, setSearchFunc] = useState('');
  const [searchVeic, setSearchVeic] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Data state
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [loadingPdfSelected, setLoadingPdfSelected] = useState(false);
  const [reportData, setReportData] = useState(null);

  // CA selection state - uses row_id for individual row selection
  const [selectedRowIds, setSelectedRowIds] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const [secRes, combRes, veicRes, postosRes] = await Promise.all([
          api.get('/secretarias'),
          api.get('/combustiveis'),
          api.get('/veiculos'),
          api.get('/postos')
        ]);
        setSecretarias(secRes.data || []);
        setCombustiveis(combRes.data || []);
        setVeiculos(veicRes.data || []);
        setPostos(postosRes.data || []);
      } catch { toast.error('Erro ao carregar dados'); }
    };
    load();
  }, []);

  // Atualizar combustiveis disponiveis quando postos mudam
  useEffect(() => {
    const fetchCombustiveisPorPosto = async () => {
      if (selectedPostos.size === 0) {
        setCombustiveisDisponiveis(combustiveis);
        return;
      }
      try {
        const ids = Array.from(selectedPostos).join(',');
        const res = await api.get(`/combustiveis/por-posto?posto_ids=${ids}`);
        const idsPermitidos = new Set(res.data);
        setCombustiveisDisponiveis(combustiveis.filter(c => idsPermitidos.has(c.id)));
        setSelectedCombustiveis(prev => {
          const filtered = new Set([...prev].filter(id => idsPermitidos.has(id)));
          return filtered;
        });
      } catch {
        setCombustiveisDisponiveis(combustiveis);
      }
    };
    fetchCombustiveisPorPosto();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPostos, combustiveis]);

  const filteredSecretarias = useMemo(() => {
    if (!searchSecretaria.trim()) return secretarias;
    const q = searchSecretaria.toLowerCase();
    return secretarias.filter(s => s.name?.toLowerCase().includes(q));
  }, [secretarias, searchSecretaria]);

  const fetchReport = useCallback(async (secId, sDate, eDate, funcIds, combIds, veicIds, postoIds) => {
    setLoading(true);
    setSelectedRowIds(new Set());
    try {
      const params = { secretaria_id: secId, start_date: sDate, end_date: eDate };
      if (funcIds && funcIds.size > 0) params.funcionario_id = Array.from(funcIds).join(',');
      if (combIds && combIds.size > 0) params.combustivel_id = Array.from(combIds).join(',');
      if (veicIds && veicIds.size > 0) params.veiculo_id = Array.from(veicIds).join(',');
      if (postoIds && postoIds.size > 0) params.posto_id = Array.from(postoIds).join(',');
      const res = await api.get('/reports/individual', { params });
      setReportData(res.data);
    } catch { toast.error('Erro ao carregar dados.'); }
    finally { setLoading(false); }
  }, []);

  const handleSelectSecretaria = async (sec) => {
    setSelectedSecretaria(sec);
    setSelectedFuncionarios(new Set());
    setSelectedCombustiveis(new Set());
    setSelectedVeiculos(new Set());
    setSelectedPostos(new Set());
    setSearchFunc('');
    setSearchVeic('');
    setShowFilters(false);
    try {
      const res = await api.get('/funcionarios');
      setFuncionarios((res.data || []).filter(f => f.secretaria_id === sec.id));
    } catch { /* */ }
    setView('dashboard');
    await fetchReport(sec.id, startDate, endDate, new Set(), new Set(), new Set(), new Set());
  };

  const applyFilters = () => {
    if (!selectedSecretaria) return;
    fetchReport(selectedSecretaria.id, startDate, endDate, selectedFuncionarios, selectedCombustiveis, selectedVeiculos, selectedPostos);
  };

  const clearFilters = () => {
    setSelectedFuncionarios(new Set());
    setSelectedCombustiveis(new Set());
    setSelectedVeiculos(new Set());
    setSelectedPostos(new Set());
    setStartDate(firstDay);
    setEndDate(lastDayStr);
    if (selectedSecretaria) {
      fetchReport(selectedSecretaria.id, firstDay, lastDayStr, new Set(), new Set(), new Set(), new Set());
    }
  };

  const buildPdfParams = () => {
    const params = { secretaria_id: selectedSecretaria.id, start_date: startDate, end_date: endDate };
    if (selectedFuncionarios.size > 0) params.funcionario_id = Array.from(selectedFuncionarios).join(',');
    if (selectedCombustiveis.size > 0) params.combustivel_id = Array.from(selectedCombustiveis).join(',');
    if (selectedVeiculos.size > 0) params.veiculo_id = Array.from(selectedVeiculos).join(',');
    if (selectedPostos.size > 0) params.posto_id = Array.from(selectedPostos).join(',');
    return params;
  };

  const handleDownloadPdf = async () => {
    setLoadingPdf(true);
    try {
      const params = buildPdfParams();
      const res = await api.get('/reports/individual-pdf', { params, responseType: 'blob', timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_${selectedSecretaria.name?.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF completo gerado com sucesso!');
    } catch { toast.error('Erro ao gerar PDF.'); }
    finally { setLoadingPdf(false); }
  };

  const handleDownloadPdfSelected = async () => {
    if (selectedRowIds.size === 0) {
      toast.error('Selecione ao menos um registro.');
      return;
    }
    setLoadingPdfSelected(true);
    try {
      const params = buildPdfParams();
      params.ids = Array.from(selectedRowIds).join(',');
      const res = await api.get('/reports/individual-pdf', { params, responseType: 'blob', timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio_selecionadas_${selectedSecretaria.name?.replace(/\s/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF com ${selectedRowIds.size} registro(s) selecionado(s) gerado!`);
    } catch { toast.error('Erro ao gerar PDF selecionadas.'); }
    finally { setLoadingPdfSelected(false); }
  };

  const toggleRowSelection = (rowId) => {
    setSelectedRowIds(prev => {
      const n = new Set(prev);
      if (n.has(rowId)) n.delete(rowId); else n.add(rowId);
      return n;
    });
  };

  const toggleSelectAllRows = () => {
    const allRowIds = records.map(r => r.row_id).filter(Boolean);
    if (selectedRowIds.size === allRowIds.length) {
      setSelectedRowIds(new Set());
    } else {
      setSelectedRowIds(new Set(allRowIds));
    }
  };

  const toggleItem = (set, setter, id) => {
    setter(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  };

  const filteredFuncionarios = useMemo(() => {
    if (!searchFunc.trim()) return funcionarios;
    const q = searchFunc.toLowerCase();
    return funcionarios.filter(f => f.name?.toLowerCase().includes(q));
  }, [funcionarios, searchFunc]);

  const filteredVeiculos = useMemo(() => {
    // Filtrar veículos pela secretaria selecionada
    const secVeiculos = selectedSecretaria ? veiculos.filter(v => v.secretaria_id === selectedSecretaria.id) : veiculos;
    if (!searchVeic.trim()) return secVeiculos;
    const q = searchVeic.toLowerCase();
    return secVeiculos.filter(v => v.plate?.toLowerCase().includes(q) || v.model?.toLowerCase().includes(q));
  }, [veiculos, searchVeic, selectedSecretaria]);

  const activeFilterCount = selectedFuncionarios.size + selectedCombustiveis.size + selectedVeiculos.size + selectedPostos.size + (startDate !== firstDay || endDate !== lastDayStr ? 1 : 0);

  // VIEW: Secretarias list
  if (view === 'secretarias') {
    return (
      <div className="space-y-6" data-testid="filtro-individual-page">
        <div>
          <h1 className="text-2xl font-bold text-[#003366]">Filtro Individual</h1>
          <p className="text-sm text-gray-500 mt-1">Selecione uma secretaria para visualizar os abastecimentos</p>
        </div>
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Buscar secretaria..." value={searchSecretaria} onChange={(e) => setSearchSecretaria(e.target.value)} className="pl-9" data-testid="search-secretaria-input" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredSecretarias.map(sec => (
            <Card key={sec.id} className="cursor-pointer hover:shadow-lg hover:border-[#003366] transition-all group" onClick={() => handleSelectSecretaria(sec)} data-testid={`secretaria-card-${sec.id}`}>
              <CardContent className="flex items-center gap-4 p-5">
                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#003366] flex items-center justify-center">
                  <Building2 size={24} className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#003366] truncate uppercase">{sec.name}</p>
                  <p className="text-xs text-gray-500">{sec.sigla || ''}</p>
                </div>
                <ChevronRight size={20} className="text-gray-300 group-hover:text-[#003366] transition-colors" />
              </CardContent>
            </Card>
          ))}
          {filteredSecretarias.length === 0 && <p className="text-gray-400 text-sm col-span-full text-center py-8">Nenhuma secretaria encontrada.</p>}
        </div>
      </div>
    );
  }

  // VIEW: Dashboard with filters
  const records = reportData?.records || [];
  const summary = reportData?.summary || {};

  return (
    <div className="space-y-4" data-testid="filtro-individual-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => { setView('secretarias'); setSelectedSecretaria(null); setReportData(null); }} data-testid="back-to-secretarias">
            <ArrowLeft size={18} className="mr-1" /> Voltar
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#003366]">{selectedSecretaria?.name?.toUpperCase()}</h1>
            <p className="text-sm text-gray-500">
              {startDate?.split('-').reverse().join('/')} a {endDate?.split('-').reverse().join('/')}
              {activeFilterCount > 0 && <span className="ml-2 text-blue-600 font-medium">({activeFilterCount} filtro{activeFilterCount > 1 ? 's' : ''} ativo{activeFilterCount > 1 ? 's' : ''})</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <Button variant="outline" size="sm" onClick={() => setShowFilters(!showFilters)} className={showFilters ? 'border-blue-500 text-blue-600' : ''} data-testid="toggle-filters-btn">
            <Filter size={16} className="mr-1" /> Filtros {activeFilterCount > 0 && `(${activeFilterCount})`}
          </Button>
          {selectedRowIds.size > 0 && (
            <Button onClick={handleDownloadPdfSelected} disabled={loadingPdfSelected} className="bg-green-700 hover:bg-green-800 text-white" data-testid="download-selected-pdf-btn">
              {loadingPdfSelected ? <><Loader2 className="animate-spin mr-2" size={16} /> Gerando...</> : <><FileText size={16} className="mr-2" /> PDF Selecionadas ({selectedRowIds.size})</>}
            </Button>
          )}
          <Button onClick={handleDownloadPdf} disabled={loadingPdf || records.length === 0} className="bg-[#003366] hover:bg-[#002244] text-white" data-testid="download-individual-pdf-btn">
            {loadingPdf ? <><Loader2 className="animate-spin mr-2" size={16} /> Gerando...</> : <><Download size={16} className="mr-2" /> PDF Completo</>}
          </Button>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <Card className="border-l-4 border-l-blue-500" data-testid="filters-panel">
          <CardContent className="p-4 space-y-4">
            {/* Period */}
            <div>
              <Label className="font-semibold text-sm mb-2 block">Periodo</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
                <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Funcionarios */}
              <div>
                <Label className="font-semibold text-sm mb-1 flex items-center gap-1">
                  <Users size={14} /> Funcionarios {selectedFuncionarios.size > 0 && <span className="text-blue-600 text-xs">({selectedFuncionarios.size})</span>}
                </Label>
                <div className="relative mb-1">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input placeholder="Buscar..." value={searchFunc} onChange={(e) => setSearchFunc(e.target.value)} className="pl-7 h-8 text-xs" data-testid="search-func-input" />
                </div>
                <div className="max-h-[150px] overflow-auto border rounded text-xs" data-testid="funcionarios-list">
                  {filteredFuncionarios.map(f => (
                    <div key={f.id} onClick={() => toggleItem(selectedFuncionarios, setSelectedFuncionarios, f.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer border-b last:border-b-0 ${selectedFuncionarios.has(f.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      data-testid={`func-item-${f.id}`}>
                      {selectedFuncionarios.has(f.id) ? <CheckSquare size={14} className="text-blue-600 flex-shrink-0" /> : <Square size={14} className="text-gray-300 flex-shrink-0" />}
                      <span className="uppercase truncate">{f.name}</span>
                    </div>
                  ))}
                  {filteredFuncionarios.length === 0 && <p className="text-gray-400 p-2">Nenhum.</p>}
                </div>
              </div>

              {/* Postos */}
              <div>
                <Label className="font-semibold text-sm mb-1 flex items-center gap-1">
                  <MapPin size={14} /> Postos {selectedPostos.size > 0 && <span className="text-blue-600 text-xs">({selectedPostos.size})</span>}
                </Label>
                <div className="max-h-[150px] overflow-auto border rounded text-xs mt-1" data-testid="postos-filter-list">
                  {postos.map(p => (
                    <div key={p.id} onClick={() => toggleItem(selectedPostos, setSelectedPostos, p.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer border-b last:border-b-0 ${selectedPostos.has(p.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      data-testid={`posto-filter-item-${p.id}`}>
                      {selectedPostos.has(p.id) ? <CheckSquare size={14} className="text-blue-600 flex-shrink-0" /> : <Square size={14} className="text-gray-300 flex-shrink-0" />}
                      <span className="uppercase truncate">{p.name}</span>
                    </div>
                  ))}
                  {postos.length === 0 && <p className="text-gray-400 p-2">Nenhum posto.</p>}
                </div>
              </div>

              {/* Combustiveis */}
              <div>
                <Label className="font-semibold text-sm mb-1 flex items-center gap-1">
                  <Fuel size={14} /> Combustiveis {selectedCombustiveis.size > 0 && <span className="text-blue-600 text-xs">({selectedCombustiveis.size})</span>}
                </Label>
                <div className="max-h-[150px] overflow-auto border rounded text-xs mt-1" data-testid="combustiveis-list">
                  {combustiveisDisponiveis.map(c => (
                    <div key={c.id} onClick={() => toggleItem(selectedCombustiveis, setSelectedCombustiveis, c.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer border-b last:border-b-0 ${selectedCombustiveis.has(c.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      data-testid={`comb-item-${c.id}`}>
                      {selectedCombustiveis.has(c.id) ? <CheckSquare size={14} className="text-blue-600 flex-shrink-0" /> : <Square size={14} className="text-gray-300 flex-shrink-0" />}
                      <span className="uppercase truncate">{c.type}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Veiculos */}
              <div>
                <Label className="font-semibold text-sm mb-1 flex items-center gap-1">
                  <Car size={14} /> Veiculos/Maquinas {selectedVeiculos.size > 0 && <span className="text-blue-600 text-xs">({selectedVeiculos.size})</span>}
                </Label>
                <div className="relative mb-1">
                  <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input placeholder="Buscar veiculo..." value={searchVeic} onChange={(e) => setSearchVeic(e.target.value)} className="pl-7 h-8 text-xs" data-testid="search-veic-input" />
                </div>
                <div className="max-h-[150px] overflow-auto border rounded text-xs" data-testid="veiculos-list">
                  {filteredVeiculos.map(v => (
                    <div key={v.id} onClick={() => toggleItem(selectedVeiculos, setSelectedVeiculos, v.id)}
                      className={`flex items-center gap-2 px-2 py-1.5 cursor-pointer border-b last:border-b-0 ${selectedVeiculos.has(v.id) ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      data-testid={`veic-item-${v.id}`}>
                      {selectedVeiculos.has(v.id) ? <CheckSquare size={14} className="text-blue-600 flex-shrink-0" /> : <Square size={14} className="text-gray-300 flex-shrink-0" />}
                      <span className="uppercase truncate">{v.plate} {v.model ? `- ${v.model}` : ''}</span>
                    </div>
                  ))}
                  {filteredVeiculos.length === 0 && <p className="text-gray-400 p-2">Nenhum veiculo encontrado.</p>}
                </div>
              </div>
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={applyFilters} disabled={loading} className="bg-[#003366] hover:bg-[#002244] text-white" data-testid="apply-filters-btn">
                {loading ? <><Loader2 className="animate-spin mr-1" size={14} /> Aplicando...</> : 'Aplicar Filtros'}
              </Button>
              <Button variant="outline" onClick={clearFilters} data-testid="clear-filters-btn">Limpar Filtros</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin text-[#003366]" size={32} />
          <span className="ml-3 text-gray-500">Carregando dados...</span>
        </div>
      )}

      {/* Summary cards */}
      {!loading && reportData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="border-t-4 border-t-[#003366]">
              <CardContent className="p-3 text-center">
                <Fuel size={24} className="mx-auto text-[#003366] mb-1" />
                <p className="text-[10px] text-gray-500 font-medium">TOTAL LITROS</p>
                <p className="text-lg font-bold text-[#003366]" data-testid="summary-litros">{formatNumber(summary.total_litros)} L</p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-green-600">
              <CardContent className="p-3 text-center">
                <DollarSign size={24} className="mx-auto text-green-600 mb-1" />
                <p className="text-[10px] text-gray-500 font-medium">VALOR TOTAL</p>
                <p className="text-lg font-bold text-green-700" data-testid="summary-valor">{formatCurrency(summary.total_valor)}</p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-amber-500">
              <CardContent className="p-3 text-center">
                <TrendingUp size={24} className="mx-auto text-amber-500 mb-1" />
                <p className="text-[10px] text-gray-500 font-medium">ABASTECIMENTOS</p>
                <p className="text-lg font-bold text-amber-600" data-testid="summary-count">{summary.total_registros || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-purple-600">
              <CardContent className="p-3 text-center">
                <Car size={24} className="mx-auto text-purple-600 mb-1" />
                <p className="text-[10px] text-gray-500 font-medium">VEICULOS</p>
                <p className="text-lg font-bold text-purple-700" data-testid="summary-veiculos">{summary.total_veiculos || 0}</p>
              </CardContent>
            </Card>
            <Card className="border-t-4 border-t-cyan-600">
              <CardContent className="p-3 text-center">
                <Users size={24} className="mx-auto text-cyan-600 mb-1" />
                <p className="text-[10px] text-gray-500 font-medium">FUNCIONARIOS</p>
                <p className="text-lg font-bold text-cyan-700" data-testid="summary-funcionarios">{summary.total_funcionarios || 0}</p>
              </CardContent>
            </Card>
          </div>

          {/* Fuel breakdown */}
          {summary.combustiveis && Object.keys(summary.combustiveis).length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Object.entries(summary.combustiveis).map(([name, data]) => (
                <Card key={name} className="border-l-4 border-l-[#003366]">
                  <CardContent className="p-3">
                    <p className="text-xs font-semibold text-[#003366] uppercase">{name}</p>
                    <p className="text-lg font-bold">{formatNumber(data.litros)} L</p>
                    <p className="text-xs text-gray-500">{formatCurrency(data.valor)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Records table */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[#003366] text-base">Registros ({records.length})</CardTitle>
                <div className="flex items-center gap-3 text-xs">
                  {records.some(r => r.status_contabil === 'vermelho') && (
                    <span className="flex items-center gap-1 text-red-500">
                      <Lock size={12} /> {records.filter(r => r.status_contabil === 'vermelho').length} desconsiderada(s)
                    </span>
                  )}
                  {selectedRowIds.size > 0 && (
                    <span className="text-green-700 font-semibold" data-testid="selection-counter">{selectedRowIds.size} selecionada(s)</span>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {records.length > 0 ? (
                <div className="overflow-auto max-h-[500px] border rounded-lg">
                  <Table>
                    <TableHeader className="sticky top-0 z-10">
                      <TableRow className="bg-[#003366]">
                        <TableHead className="text-white w-[40px]">
                          <div onClick={toggleSelectAllRows} className="cursor-pointer flex items-center justify-center" data-testid="select-all-ca-checkbox">
                            {records.length > 0 && selectedRowIds.size === records.length
                              ? <CheckSquare size={16} className="text-green-300" />
                              : <Square size={16} className="text-white/60" />}
                          </div>
                        </TableHead>
                        <TableHead className="text-white font-semibold">CA</TableHead>
                        <TableHead className="text-white font-semibold">Status</TableHead>
                        <TableHead className="text-white font-semibold">Data</TableHead>
                        <TableHead className="text-white font-semibold">Nome</TableHead>
                        <TableHead className="text-white font-semibold">Maq./Veiculo</TableHead>
                        <TableHead className="text-white font-semibold">Posto</TableHead>
                        <TableHead className="text-white font-semibold">Combustivel</TableHead>
                        <TableHead className="text-white font-semibold text-right">Litros</TableHead>
                        <TableHead className="text-white font-semibold text-right">Preco Unit.</TableHead>
                        <TableHead className="text-white font-semibold text-right">Valor Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {records.map((rec, idx) => {
                        const isBlocked = rec.status_contabil === 'vermelho';
                        const isSelected = selectedRowIds.has(rec.row_id);
                        const postoColor = POSTO_COLORS[rec.posto] || null;
                        return (
                          <TableRow
                            key={rec.row_id || idx}
                            className={`cursor-pointer transition-colors ${isBlocked ? 'opacity-60 bg-red-50/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isSelected ? 'ring-1 ring-inset ring-green-400 bg-green-50/40' : ''}`}
                            onClick={() => toggleRowSelection(rec.row_id)}
                            data-testid={`individual-row-${idx}`}
                          >
                            <TableCell>
                              <div className="flex items-center justify-center" data-testid={`ca-checkbox-${idx}`}>
                                {isSelected
                                  ? <CheckSquare size={16} className="text-green-600" />
                                  : <Square size={16} className="text-gray-300" />}
                              </div>
                            </TableCell>
                            <TableCell className={`font-bold ${isBlocked ? 'text-red-400 line-through' : 'text-[#CC0000]'}`}>{rec.ca}</TableCell>
                            <TableCell>
                              {isBlocked
                                ? <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium"><Lock size={12} /> Bloqueada</span>
                                : <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">Ativa</span>}
                            </TableCell>
                            <TableCell className={isBlocked ? 'text-gray-400' : ''}>{rec.date}</TableCell>
                            <TableCell className={`uppercase font-medium ${isBlocked ? 'text-gray-400' : ''}`}>{rec.funcionario_nome}</TableCell>
                            <TableCell className={`uppercase ${isBlocked ? 'text-gray-400' : ''}`}>{rec.veiculo}</TableCell>
                            <TableCell>
                              <span className="uppercase text-xs font-semibold" style={postoColor ? { color: postoColor.bg } : {}}>{rec.posto || '-'}</span>
                            </TableCell>
                            <TableCell className={`uppercase ${isBlocked ? 'text-gray-400' : ''}`}>{rec.combustivel}</TableCell>
                            <TableCell className={`text-right ${isBlocked ? 'text-gray-400' : ''}`}>{formatNumber(rec.litros)} L</TableCell>
                            <TableCell className={`text-right ${isBlocked ? 'text-gray-400' : ''}`}>{formatCurrency(rec.preco_unitario)}</TableCell>
                            <TableCell className={`text-right font-semibold ${isBlocked ? 'text-red-400 line-through' : 'text-green-700'}`}>{formatCurrency(rec.valor_total)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-center text-gray-400 py-8">Nenhum registro encontrado no periodo.</p>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
